/**
 * LeaveTable Component
 * Reusable table component for displaying leave records
 */

import React from 'react';
import { Text, Group, Badge, Stack } from '@mantine/core';
import { BenchTable } from '../../pages/UI';
import { LeaveRecord } from '../../../types/leave.types';
import { LEAVE_TYPES_CONFIG } from '../../../constants/leave.constants';
import { formatDate, getStatusColor } from '../../../utils/leave.utils';

interface LeaveTableProps {
    leaves: LeaveRecord[];
    emptyMessage?: string;
}

export const LeaveTable: React.FC<LeaveTableProps> = ({
    leaves,
    emptyMessage = 'No leave records found'
}) => {
    if (leaves.length === 0) {
        return (
            <Text align="center" color="dimmed" py="xl">
                {emptyMessage}
            </Text>
        );
    }

    // Helper function to get leave type config by code
    const getLeaveTypeByCode = (code: string) => {
        return LEAVE_TYPES_CONFIG.find(l => l.code === code);
    };

    return (
        <BenchTable>
            <thead>
                <tr>

                    <th>Date</th>
                    <th>Leave Type</th>
                    <th>Duration</th>
                    <th>Reason</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                {leaves.map((leave) => {
                    const leaveConfig = getLeaveTypeByCode(leave?.leave_type);
                    const docDate = leave?.leave_date?.toDate?.() || (leave?.leave_date ? new Date(leave.leave_date) : null);
                    const isDateValid = docDate instanceof Date && !isNaN(docDate.getTime());
                    const formattedDate = isDateValid ? formatDate(docDate.toISOString()) : 'N/A';
                    const displayStatus = leave.leave_approved_status || 'pending';

                    return (
                        <tr key={leave.id}>

                            <td>
                                <Text size="sm">{formattedDate}</Text>
                            </td> <td>
                                <Group spacing="xs">
                                    <Text weight={500}>{leaveConfig?.label || leave.leave_type}</Text>
                                </Group>
                            </td>
                            {/* <td>
                                <Text size="sm">{formattedDate}</Text>
                            </td> */}
                            <td>
                                {(() => {
                                    // Calculate total duration (each record is one day)
                                    let duration = 0;
                                    if (leave.fh_status) duration += 0.5;
                                    if (leave.sh_status) duration += 0.5;

                                    const isHalfDay = duration === 0.5;

                                    return (
                                        <Stack spacing={2}>
                                            <Text>{duration} day</Text>
                                            {isHalfDay && (
                                                <Text size="xs" color="dimmed">
                                                    {leave.fh_status ? 'First Half' : 'Second Half'}
                                                </Text>
                                            )}
                                        </Stack>
                                    );
                                })()}
                            </td>
                            <td>
                                <Text size="sm" lineClamp={1}>{leave.reason || 'N/A'}</Text>
                            </td>
                            <td>
                                <Badge
                                    color={getStatusColor(displayStatus)}
                                    variant="light"
                                >
                                    {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                </Badge>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </BenchTable>
    );
};
